import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { listEnrollments } from "../api/rest";
import { useSiteStore, ROLE_RANK } from "../stores/siteStore";
import { NAV_SECTIONS, type NavItem } from "./navConfig";
import { navItemVisible } from "./navGating";
import { useGateContext } from "./routeGating";
import { useProvisioning } from "../hooks/useProvisioning";
import { NavIcon } from "./NavIcons";
import styles from "./Sidebar.module.css";

export interface SidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Called when a nav item is chosen (the mobile drawer closes itself). */
  onNavigate?: () => void;
}

export function Sidebar({ collapsed, onToggleCollapsed, onNavigate }: SidebarProps) {
  const { t } = useTranslation();
  const hasWarehouse = useSiteStore((s) => s.availableWarehouses.length > 0);
  // An operator (granted, cannot provision) never gets the setup link: the page
  // behind it has nothing for them (ADR 408).
  const { role: setupRole } = useProvisioning();
  // The same context the routes are gated with (ADR 395) — one gate, one set of inputs.
  const gateCtx = useGateContext();

  // The pending-devices badge belongs to the Dispositivos item (site-admin and up, ADR 120):
  // anyone below it got a 403 every 30 s for a badge they could never see (ADR 408 note).
  const canSeeDevices = gateCtx.isGlobalAdmin || gateCtx.roleRank >= ROLE_RANK["site-admin"];
  const { data: pending } = useQuery({
    queryKey: ["enrollments", "pending"],
    queryFn: () => listEnrollments("pending"),
    enabled: canSeeDevices,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
  const pendingCount = pending?.length ?? 0;

  // Provisioning + permission gating (ADR 175 + 120), see navGating.ts.
  const visible = (item: NavItem) => navItemVisible(item, gateCtx);

  return (
    <aside className={collapsed ? `${styles.rail} ${styles.collapsed}` : styles.rail}>
      <nav aria-label={t("a11y.mainNav")} className={styles.nav}>
        {/* Until a warehouse exists the app isn't operational yet — lead with
            the guided setup so the admin has a clear first move (ADR 175). */}
        {!hasWarehouse && setupRole !== "operator" && (
          <NavLink
            to="/setup"
            onClick={onNavigate}
            title={collapsed ? t("nav.gettingStarted") : undefined}
            className={({ isActive }) => (isActive ? styles.linkActive : styles.link)}
          >
            <NavIcon name="guide" className={styles.icon} />
            <span className={styles.label}>{t("nav.gettingStarted")}</span>
          </NavLink>
        )}
        {NAV_SECTIONS.map((sec) => {
          const items = sec.items.filter(visible);
          if (items.length === 0) return null; // whole section hidden when nothing in it qualifies
          return (
            <div key={sec.id} role="group" aria-labelledby={`navsec-${sec.id}`} className={styles.section}>
              <div id={`navsec-${sec.id}`} className={styles.sectionTitle}>
                <span className={styles.sectionLabel}>{t(sec.labelKey)}</span>
              </div>
              {items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onNavigate}
                  title={collapsed ? t(item.labelKey) : undefined}
                  className={({ isActive }) => (isActive ? styles.linkActive : styles.link)}
                >
                  <NavIcon name={item.icon} className={styles.icon} />
                  <span className={styles.label}>{t(item.labelKey)}</span>
                  {item.badge === "pendingDevices" && pendingCount > 0 && (
                    <span className={styles.badge} aria-label={t("nav.pendingBadge", { count: pendingCount })}>
                      {pendingCount}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>
      <button
        type="button"
        className={styles.collapseBtn}
        onClick={onToggleCollapsed}
        aria-label={collapsed ? t("a11y.expandNav") : t("a11y.collapseNav")}
      >
        <NavIcon name="chevron" className={collapsed ? styles.chevronFlipped : undefined} />
      </button>
    </aside>
  );
}
